import { useState, useEffect, useMemo } from 'react';
import { Loader } from '@/components/shared';
import { useGetStreams, useGetUpcomingLives } from '@/lib/react-query/queries';
import { useUserContext } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { KYCVerificationModal } from '@/modals/KYCVerificationModal';
import { useToast } from '@/components/ui/use-toast';
import { getKYCStatus } from '@/lib/appwrite/api';
import ScheduleLiveModal from './ScheduleLiveModal';
import { format, isToday, isTomorrow, addDays } from 'date-fns';
import { 
  Calendar, 
  Clock, 
  Eye, 
  Gift, 
  Heart, 
  Play, 
  ShoppingBag, 
  Star, 
  TrendingUp,
  Users,
  Zap,
  Timer,
  Percent,
  Sparkles,
  Shield,
  CheckCircle,
  AlertCircle,
  Edit,
  Trash2,
} from 'lucide-react';
import { databases } from '@/lib/appwrite/config';
import { appwriteConfig } from '@/lib/appwrite/config';
import { createLiveStreamFromScheduled, deleteScheduledLive } from '@/services/liveStreamService';

const tabs = [
  { id: 'live', label: 'Live Now', count: 0, icon: Zap, color: 'text-rose-500' },
  { id: 'upcoming', label: 'Upcoming', count: 0, icon: Calendar, color: 'text-blue-500' },
  { id: 'trending', label: 'Trending', count: 0, icon: TrendingUp, color: 'text-orange-500' },
  { id: 'ended', label: 'Recent', count: 0, icon: Clock, color: 'text-gray-500' }
];

export default function EnhancedLivePage() {
  const { user } = useUserContext();
  const { data: liveStreams, isLoading: isStreamLoading } = useGetStreams(50);
  const { data: scheduledStreams, isLoading: isScheduledLoading, refetch: refetchScheduled } = useGetUpcomingLives();
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('live');
  const [realtimeViewers, setRealtimeViewers] = useState<Record<string, number>>({});
  const [featuredDiscounts, setFeaturedDiscounts] = useState<Record<string, any>>({});
  const { toast } = useToast();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStream, setEditingStream] = useState<any>(null);
  const [goingLiveFromScheduled, setGoingLiveFromScheduled] = useState<string | null>(null);

  // Check KYC status on component mount
  useEffect(() => {
    const checkKYCStatus = async () => {
      try {
        const status = await getKYCStatus(user.id);
        setKycStatus(status);
      } catch (error) {
        console.error('Failed to check KYC status:', error);
      }
    };
    
    if (user?.id) {
      checkKYCStatus();
    }
  }, [user?.id]);

  // Real-time viewer tracking
  useEffect(() => {
    if (!liveStreams?.documents) return;

    const liveStreamIds = liveStreams.documents
      .filter(stream => stream.status === 'live')
      .map(stream => stream.$id);

    if (liveStreamIds.length === 0) return;

    // Subscribe to real-time updates for all live streams
    const subscriptions = liveStreamIds.map(streamId => {
      const channel = `collections.${appwriteConfig.liveStreams}.documents.${streamId}`;
      
      return databases.client.subscribe(channel, (response: any) => {
        if (response.payload?.viewers !== undefined) {
          setRealtimeViewers(prev => ({
            ...prev,
            [streamId]: parseInt(response.payload.viewers) || 0
          }));
        }
      });
    });

    // Cleanup subscriptions
    return () => {
      subscriptions.forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [liveStreams?.documents]);

  // Initialize viewer counts from database
  useEffect(() => {
    if (!liveStreams?.documents) return;

    const initialViewers: Record<string, number> = {};
    liveStreams.documents.forEach(stream => {
      if (stream.status === 'live') {
        initialViewers[stream.$id] = parseInt(stream.viewers || '0') || 0;
      }
    });
    
    setRealtimeViewers(initialViewers);
  }, [liveStreams?.documents]);

  // Extract featured discount info
  useEffect(() => {
    if (!scheduledStreams?.documents && !liveStreams?.documents) return;

    const discountInfo: Record<string, any> = {};
    
    // From scheduled streams
    scheduledStreams?.documents?.forEach((stream: any) => {
      if (stream.discountPercent && stream.discountMaxUsers) {
        discountInfo[stream.$id] = {
          percent: stream.discountPercent,
          maxUsers: stream.discountMaxUsers,
          used: 0,
          type: 'upcoming'
        };
      }
    });

    // From live streams
    liveStreams?.documents?.forEach((stream: any) => {
      if (stream.scheduledDiscountPercent && stream.scheduledDiscountMaxUsers) {
        const remaining = parseInt(stream.scheduledDiscountMaxUsers) - parseInt(stream.scheduledDiscountUsedCount || '0');
        if (remaining > 0) {
          discountInfo[stream.$id] = {
            percent: stream.scheduledDiscountPercent,
            maxUsers: stream.scheduledDiscountMaxUsers,
            used: parseInt(stream.scheduledDiscountUsedCount || '0'),
            remaining,
            type: 'live'
          };
        }
      }
    });

    setFeaturedDiscounts(discountInfo);
  }, [scheduledStreams?.documents, liveStreams?.documents]);

  const handleGoLive = async () => {
    try {
      setKycLoading(true);
      
      const kycStatus = await getKYCStatus(user.id);
      
      if (kycStatus.panVerified && kycStatus.aadhaarVerified && kycStatus.bankVerified) {
        window.location.href = '/live/create';
      } else {
        setShowKYCModal(true);
      }
    } catch (error) {
      toast({
        title: 'Error checking verification status',
        description: 'Please try again later',
        variant: 'destructive'
      });
      console.error('KYC check failed:', error);
    } finally {
      setKycLoading(false);
    }
  };

const handleGoLiveFromScheduled = async (scheduledStream: any) => {
  try {
    setGoingLiveFromScheduled(scheduledStream.$id);
    
    // Extract discount info if available
    const scheduledDiscount = scheduledStream.discountPercent && scheduledStream.discountMaxUsers ? {
      maxUsers: scheduledStream.discountMaxUsers,
      percent: scheduledStream.discountPercent
    } : undefined;

    // Use the existing createLiveStreamFromScheduled function that you already have
    const liveStreamData = {
      scheduledStreamId: scheduledStream.$id,
      sellerId: scheduledStream.sellerId,
      sellerName: user.name,
      title: scheduledStream.title,
      description: scheduledStream.description || "",
      featuredProducts: scheduledStream.products || [],
      thumbnailUrl: scheduledStream.productImage,
      scheduledDiscount,
    };

    const newLiveStream = await createLiveStreamFromScheduled(liveStreamData);
    
    toast({
      title: '🎉 You\'re Live!',
      description: 'Your scheduled stream is now broadcasting',
      variant: 'default'
    });

    // Refresh the data
    refetchScheduled();
    
    // 🔧 FIX: Redirect to the actual live stream page, not creator page
    window.location.href = `/live/${newLiveStream.$id}`;
    
  } catch (error) {
    console.error('Error going live from scheduled:', error);
    toast({
      title: 'Failed to go live',
      description: 'Please try again or contact support',
      variant: 'destructive'
    });
  } finally {
    setGoingLiveFromScheduled(null);
  }
};

  const handleEditScheduled = (stream: any) => {
    setEditingStream(stream);
    setShowEditModal(true);
  };

  const handleDeleteScheduled = async (streamId: string) => {
    try {
      await deleteScheduledLive(streamId);
      toast({
        title: 'Stream deleted',
        description: 'Your scheduled stream has been removed',
        variant: 'default'
      });
      refetchScheduled();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete stream',
        variant: 'destructive'
      });
    }
  };

  const isLoading = isStreamLoading || isScheduledLoading;

  // Helper function to check if scheduled time has passed
  const isScheduledTimePassed = (scheduledAt: string) => {
    return new Date() > new Date(scheduledAt);
  };

  // Check if scheduled time is within 15 minutes
  const canGoLiveNow = (scheduledAt: string) => {
    const scheduledTime = new Date(scheduledAt);
    const now = new Date();
    const timeDiff = scheduledTime.getTime() - now.getTime();
    const minutesDiff = timeDiff / (1000 * 60);
    
    // Can go live 15 minutes before scheduled time or anytime after
    return minutesDiff <= 15;
  };

  // Smart filtering with enhanced logic
  const processedStreams = useMemo(() => {
    const live = liveStreams?.documents?.filter(stream => stream.status === 'live') || [];
    const ended = liveStreams?.documents?.filter(stream => stream.status === 'ended') || [];
    
    const scheduledWithTimeCheck = scheduledStreams?.documents?.map((stream: any) => {
      if (stream.status === 'upcoming' && isScheduledTimePassed(stream.scheduledAt)) {
        return { ...stream, status: 'missed', originalStatus: 'upcoming', isUIConverted: true };
      }
      return stream;
    }) || [];

    const upcoming = scheduledWithTimeCheck.filter((stream: any) => 
      stream.status === 'upcoming' && stream.isActivated === true
    );

    // Trending logic: live streams with high engagement
    const trending = live.filter(stream => {
      const viewers = realtimeViewers[stream.$id] || parseInt(stream.viewers || '0') || 0;
      const likes = parseInt(stream.likes || '0') || 0;
      return viewers > 10 || likes > 5; // Customize trending criteria
    }).sort((a, b) => {
      const aScore = (realtimeViewers[a.$id] || 0) + parseInt(a.likes || '0');
      const bScore = (realtimeViewers[b.$id] || 0) + parseInt(b.likes || '0');
      return bScore - aScore;
    });

    return { live, ended, upcoming, trending };
  }, [liveStreams?.documents, scheduledStreams?.documents, realtimeViewers]);

  // Update tab counts
  const tabsWithCounts = tabs.map(tab => ({
    ...tab,
    count: tab.id === 'live' ? processedStreams.live.length :
           tab.id === 'ended' ? processedStreams.ended.length :
           tab.id === 'upcoming' ? processedStreams.upcoming.length :
           tab.id === 'trending' ? processedStreams.trending.length : 0
  }));

  const getCurrentStreams = () => {
    switch (activeTab) {
      case 'live': return processedStreams.live;
      case 'ended': return processedStreams.ended;
      case 'upcoming': return processedStreams.upcoming;
      case 'trending': return processedStreams.trending;
      default: return [];
    }
  };

  // Enhanced format functions
  const formatViewerCount = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const getScheduleTimeDisplay = (scheduledAt: string) => {
    const date = new Date(scheduledAt);
    const now = new Date();
    
    if (isToday(date)) {
      return {
        primary: 'Today',
        secondary: format(date, 'h:mm a'),
        urgent: true
      };
    } else if (isTomorrow(date)) {
      return {
        primary: 'Tomorrow',
        secondary: format(date, 'h:mm a'),
        urgent: true
      };
    } else if (date <= addDays(now, 7)) {
      return {
        primary: format(date, 'EEEE'),
        secondary: format(date, 'h:mm a'),
        urgent: false
      };
    } else {
      return {
        primary: format(date, 'MMM d'),
        secondary: format(date, 'h:mm a'),
        urgent: false
      };
    }
  };

  // Enhanced stream card rendering
  const renderStreamCard = (stream: any, isScheduled = false) => {
    const discount = featuredDiscounts[stream.$id];
    const viewerCount = realtimeViewers[stream.$id] || parseInt(stream.viewers || '0') || 0;
    const timeDisplay = isScheduled ? getScheduleTimeDisplay(stream.scheduledAt) : null;
    const canGoLive = isScheduled ? canGoLiveNow(stream.scheduledAt) : false;
    const isMyStream = stream.sellerId === user.id;

    const getStatusElements = () => {
      if (isScheduled) {
        if (stream.status === 'upcoming') {
          return (
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center">
                <Calendar size={10} className="mr-1" />
                SCHEDULED
              </div>
              {discount && (
                <div className="bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center">
                  <Gift size={10} className="mr-1" />
                  {discount.percent}% OFF
                </div>
              )}
            </div>
          );
        }
      } else {
        if (stream.status === 'live') {
          return (
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              <div className="bg-gradient-to-r from-rose-500 to-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center relative">
                <div className="w-2 h-2 bg-white rounded-full mr-1.5 animate-pulse"></div>
                LIVE
              </div>
              {discount && discount.remaining > 0 && (
                <div className="bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center animate-pulse">
                  <TrendingUp size={10} className="mr-1" />
                  {discount.remaining} LEFT
                </div>
              )}
            </div>
          );
        } else if (stream.status === 'ended') {
          return (
            <div className="absolute top-2 left-2">
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                ENDED
              </div>
            </div>
          );
        }
      }
      return null;
    };

    const cardContent = (
      <div className={`group relative border border-gray-200 rounded-xl overflow-hidden bg-white transition-all duration-300 hover:shadow-lg ${
        stream.status === 'live' 
          ? 'border-rose-200 hover:border-rose-300' 
          : isScheduled 
            ? timeDisplay?.urgent 
              ? 'border-blue-200 hover:border-blue-300'
              : 'border-gray-200 hover:border-gray-300'
            : 'border-gray-200 hover:border-gray-300'
      } ${stream.status === 'ended' ? 'opacity-75' : ''}`}>
        
        {/* Enhanced Image Container */}
        <div className="relative aspect-video overflow-hidden">
          <img
            src={isScheduled ? stream.productImage : stream.thumbnailUrl}
            alt={stream.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.src = '/default-stream-thumbnail.jpg';
            }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>
          
          {/* Status Elements */}
          {getStatusElements()}
          
          {/* Viewer Count for Live Streams */}
          {stream.status === 'live' && (
            <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-full flex items-center">
              <Eye size={10} className="mr-1" />
              {formatViewerCount(viewerCount)}
            </div>
          )}

          {/* Play Button Overlay */}
          {stream.status === 'live' && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Play size={20} className="text-white ml-1" fill="currentColor" />
              </div>
            </div>
          )}

          {/* Urgent Time Indicator for Scheduled */}
          {isScheduled && timeDisplay?.urgent && (
            <div className="absolute bottom-2 right-2 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center animate-pulse">
              <Timer size={10} className="mr-1" />
              {timeDisplay.primary}
            </div>
          )}

          {/* Action Buttons for My Scheduled Streams */}
          {isScheduled && isMyStream && (
            <div className="absolute bottom-2 left-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleEditScheduled(stream);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-full text-xs transition-colors"
              >
                <Edit size={10} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (confirm('Delete this scheduled stream?')) {
                    handleDeleteScheduled(stream.$id);
                  }
                }}
                className="bg-rose-500 hover:bg-rose-600 text-white p-1.5 rounded-full text-xs transition-colors"
              >
                <Trash2 size={10} />
              </button>
            </div>
          )}
        </div>

        {/* Enhanced Content Section */}
        <div className="p-3 space-y-2">
          {/* Seller Info */}
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center relative flex-shrink-0">
              <span className="text-xs font-bold text-white">
                {stream.sellerName?.[0]?.toUpperCase() || 'B'}
              </span>
              {stream.status === 'live' && (
                <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-rose-500 rounded-full border border-white">
                  <div className="w-full h-full bg-rose-500 rounded-full animate-ping"></div>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate text-sm leading-tight">
                {stream.title}
              </h3>
              <p className="text-gray-500 text-xs truncate">
                by {stream.sellerName}
              </p>
            </div>
            {stream.status === 'live' && (
              <div className="flex items-center text-rose-500 flex-shrink-0">
                <Heart size={12} className="mr-1" />
                <span className="text-xs font-medium">
                  {formatViewerCount(parseInt(stream.likes || '0'))}
                </span>
              </div>
            )}
          </div>

          {/* Time Display for Scheduled */}
          {isScheduled && timeDisplay && (
            <div className={`flex items-center justify-between p-2 rounded-lg ${
              timeDisplay.urgent 
                ? 'bg-gradient-to-r from-orange-50 to-rose-50 border border-orange-200' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-center space-x-2">
                <Clock size={14} className={timeDisplay.urgent ? 'text-orange-600' : 'text-gray-600'} />
                <div>
                  <div className={`font-semibold text-xs ${
                    timeDisplay.urgent ? 'text-orange-700' : 'text-gray-700'
                  }`}>
                    {timeDisplay.primary}
                  </div>
                  <div className="text-xs text-gray-500">
                    {timeDisplay.secondary}
                  </div>
                </div>
              </div>
              {timeDisplay.urgent && (
                <div className="text-xs font-medium text-orange-600 animate-pulse">
                  Soon!
                </div>
              )}
            </div>
          )}

          {/* Go Live Now Button for Scheduled Streams */}
          {isScheduled && isMyStream && canGoLive && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleGoLiveFromScheduled(stream);
              }}
              disabled={goingLiveFromScheduled === stream.$id}
              className="w-full bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {goingLiveFromScheduled === stream.$id ? (
                <>
                  <Loader />
                  Going Live...
                </>
              ) : (
                <>
                  <Zap size={14} className="mr-2" />
                  Go Live Now
                </>
              )}
            </button>
          )}

          {/* Discount Info */}
          {discount && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
                    <Percent size={10} className="text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-orange-700">
                      {discount.percent}% OFF
                    </div>
                    <div className="text-xs text-orange-600">
                      Early bird special
                    </div>
                  </div>
                </div>
                {discount.type === 'live' && (
                  <div className="text-right">
                    <div className="text-xs text-orange-600">
                      {discount.remaining} left
                    </div>
                    <div className="text-xs text-gray-500">
                      of {discount.maxUsers}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats Row */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-3">
              {stream.status === 'live' && (
                <>
                  <div className="flex items-center">
                    <Users size={10} className="mr-1" />
                    {formatViewerCount(viewerCount)}
                  </div>
                  <div className="flex items-center">
                    <ShoppingBag size={10} className="mr-1" />
                    {stream.purchases || '0'}
                  </div>
                </>
              )}
              {isScheduled && (
                <div className="flex items-center">
                  <Star size={10} className="mr-1" />
                  {stream.products?.length || '0'} products
                </div>
              )}
            </div>
            
            {activeTab === 'trending' && (
              <div className="flex items-center text-orange-500">
                <TrendingUp size={10} className="mr-1" />
                <span className="font-medium">Trending</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );

    // Enhanced clickability
    if (stream.status === 'live' || stream.status === 'ended') {
      return (
        <Link key={stream.$id} to={`/live/${stream.$id}`} className="block">
          {cardContent}
        </Link>
      );
    }

    return <div key={stream.$id}>{cardContent}</div>;
  };

  // Enhanced empty states
  const getEmptyStateConfig = () => {
    const isVerified = kycStatus?.panVerified && kycStatus?.aadhaarVerified && kycStatus?.bankVerified;
    
    switch (activeTab) {
      case 'live':
        return {
          icon: Zap,
          title: 'No live streams right now',
          description: isVerified 
            ? 'Be the first to go live and showcase your products!'
            : 'Join our verified creator community and start selling instantly!',
          primaryAction: { 
            text: isVerified ? 'Go Live Now' : '⚡ Get Verified & Go Live', 
            action: handleGoLive 
          },
          secondaryAction: { text: 'Schedule Stream', action: () => setShowScheduleModal(true) }
        };
      case 'upcoming':
        return {
          icon: Calendar,
          title: 'No upcoming streams',
          description: 'Schedule your next live shopping session to build anticipation!',
          primaryAction: { text: 'Schedule Now', action: () => setShowScheduleModal(true) }
        };
      case 'trending':
        return {
          icon: TrendingUp,
          title: 'No trending streams',
          description: 'Streams with high engagement will appear here.',
          primaryAction: { text: 'Go Live', action: handleGoLive }
        };
      case 'ended':
        return {
          icon: Clock,
          title: 'No recent streams',
          description: 'Your completed live streams will appear here.',
        };
      default:
        return {
          icon: Sparkles,
          title: 'Get started',
          description: 'Start your live selling journey!',
          primaryAction: { text: 'Go Live', action: handleGoLive }
        };
    }
  };

  const currentStreams = getCurrentStreams();
  const emptyConfig = getEmptyStateConfig();
  const isVerified = kycStatus?.panVerified && kycStatus?.aadhaarVerified && kycStatus?.bankVerified;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-Optimized Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Live Shopping</h1>
                <p className="text-sm text-gray-500">Discover products through live streams</p>
              </div>
              
              {/* KYC Status Badge */}
              {kycStatus && (
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                  isVerified 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                }`}>
                  {isVerified ? (
                    <>
                      <CheckCircle size={12} />
                      <span>Verified</span>
                    </>
                  ) : (
                    <>
                      <Shield size={12} />
                      <span>Get Verified</span>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Enhanced Action Buttons with FOMO */}
            <div className="flex space-x-2">
              <button
                onClick={() => setShowScheduleModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm"
              >
                <Calendar size={16} className="mr-2" />
                Schedule Live
              </button>
              <button
                onClick={handleGoLive}
                disabled={kycLoading}
                className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm ${
                  isVerified
                    ? 'bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white shadow-lg'
                    : 'bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white animate-pulse shadow-lg'
                }`}
              >
                {kycLoading ? (
                  <>
                    <Loader />
                    <span className="ml-2">Checking...</span>
                  </>
                ) : isVerified ? (
                  <>
                    <Zap size={16} className="mr-2" />
                    Go Live Now
                  </>
                ) : (
                  <>
                    <Shield size={16} className="mr-2" />
                    Quick Verify & Go Live
                  </>
                )}
              </button>
            </div>
            
            {/* Verification FOMO Banner */}
            {!isVerified && kycStatus && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <div className="w-5 h-5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle size={12} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-orange-800">
                      🚀 Join our Verified Creators
                    </h4>
                    <p className="text-xs text-orange-700 mt-1">
                      Complete verification in just 2 minutes and start earning instantly! 
                      <span className="font-semibold"> No waiting, no delays.</span>
                    </p>
                    <div className="flex items-center mt-2 space-x-4 text-xs">
                      <div className={`flex items-center ${kycStatus.panVerified ? 'text-green-600' : 'text-gray-500'}`}>
                        {kycStatus.panVerified ? <CheckCircle size={10} className="mr-1" /> : <div className="w-2 h-2 bg-gray-300 rounded-full mr-1" />}
                        PAN {kycStatus.panVerified ? '✓' : ''}
                      </div>
                      <div className={`flex items-center ${kycStatus.aadhaarVerified ? 'text-green-600' : 'text-gray-500'}`}>
                        {kycStatus.aadhaarVerified ? <CheckCircle size={10} className="mr-1" /> : <div className="w-2 h-2 bg-gray-300 rounded-full mr-1" />}
                        Aadhaar {kycStatus.aadhaarVerified ? '✓' : ''}
                      </div>
                      <div className={`flex items-center ${kycStatus.bankVerified ? 'text-green-600' : 'text-gray-500'}`}>
                        {kycStatus.bankVerified ? <CheckCircle size={10} className="mr-1" /> : <div className="w-2 h-2 bg-gray-300 rounded-full mr-1" />}
                        Bank {kycStatus.bankVerified ? '✓' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-4">
        {/* Mobile-Optimized Tab Navigation */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            {/* Scrollable Tabs */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 overflow-x-auto flex-1">
              {tabsWithCounts.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-md font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <Icon size={14} className={activeTab === tab.id ? tab.color : 'text-gray-400'} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                    {tab.count > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                        activeTab === tab.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Live Stats Badge */}
            {processedStreams.live.length > 0 && (
              <div className="ml-3 flex items-center space-x-1 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1">
                <div className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse"></div>
                <span className="text-rose-600 font-medium text-xs">
                  {processedStreams.live.length}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-16">
            <div className="text-center">
              <Loader />
              <p className="text-gray-500 mt-3 text-sm">Loading amazing streams...</p>
            </div>
          </div>
        )}

        {/* Content Section */}
        {!isLoading && (
          <>
            {currentStreams.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {currentStreams.map((stream) => 
                  renderStreamCard(stream, activeTab === 'upcoming')
                )}
              </div>
            ) : (
              /* Enhanced Empty State */
              <div className="text-center py-16">
                <div className="max-w-sm mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <emptyConfig.icon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{emptyConfig.title}</h3>
                  <p className="text-gray-500 mb-6 text-sm">{emptyConfig.description}</p>
                  
                  <div className="flex flex-col space-y-2">
                    {emptyConfig.primaryAction && (
                      <button
                        onClick={emptyConfig.primaryAction.action}
                        className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
                          !isVerified && activeTab === 'live'
                            ? 'bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white animate-pulse'
                            : 'bg-rose-500 hover:bg-rose-600 text-white'
                        }`}
                      >
                        {emptyConfig.primaryAction.text}
                      </button>
                    )}
                    {emptyConfig.secondaryAction && (
                      <button
                        onClick={emptyConfig.secondaryAction.action}
                        className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
                      >
                        {emptyConfig.secondaryAction.text}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showKYCModal && (
        <KYCVerificationModal
          userId={user.id}
          onComplete={(status) => {
            setShowKYCModal(false);
            setKycStatus(status);
            if (status.pan && status.aadhaar && status.bank) {
              toast({
                title: '🎉 Welcome to Verified Creators!',
                description: 'You can now go live and start earning instantly',
                variant: 'default'
              });
            }
          }}
          onClose={() => setShowKYCModal(false)}
        />
      )}

      {showScheduleModal && (
        <ScheduleLiveModal
          sellerId={user.id}
          onClose={() => setShowScheduleModal(false)}
          onScheduleSuccess={() => {
            setActiveTab('upcoming');
            refetchScheduled();
            toast({
              title: 'Stream Scheduled! 📅',
              description: 'Your followers will be notified before you go live',
              variant: 'default'
            });
          }}
        />
      )}

      {showEditModal && editingStream && (
        <ScheduleLiveModal
          sellerId={user.id}
          editMode={true}
          editData={editingStream}
          onClose={() => {
            setShowEditModal(false);
            setEditingStream(null);
          }}
          onScheduleSuccess={() => {
            setShowEditModal(false);
            setEditingStream(null);
            refetchScheduled();
            toast({
              title: 'Stream Updated! ✏️',
              description: 'Your followers will be notified about the changes',
              variant: 'default'
            });
          }}
        />
      )}
    </div>
  );
}